import type { FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import { ArrowUpRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import CropMarks from '@/components/generals/crop-marks'
import SectionLabel from '@/components/generals/section-label'
import SplitReveal from '@/components/motion/split-reveal'
import TransitionLink from '@/components/motion/transition-link'
import { gsap, MOTION_QUERIES } from '@/lib/motion'
import { services } from '@/constants/content'

// The services as a typographic index. Hovering a row runs a highlighter across
// it, stretches the name on its width axis and floats a framed preview of the
// work beside the pointer.
const ServicesIndex: FC = (): ReactNode => {
    const listRef = useRef<HTMLUListElement>(null)
    const previewRef = useRef<HTMLDivElement>(null)
    const [active, setActive] = useState(0)
    const [previous, setPrevious] = useState(-1)
    const [visible, setVisible] = useState(false)

    useEffect(() => {
        const list = listRef.current
        const preview = previewRef.current

        if (!list || !preview || !window.matchMedia(MOTION_QUERIES.fine).matches) return

        gsap.set(preview, { xPercent: -50, yPercent: -50 })

        const moveX = gsap.quickTo(preview, 'x', { duration: 0.7, ease: 'power3.out' })
        const moveY = gsap.quickTo(preview, 'y', { duration: 0.7, ease: 'power3.out' })
        let placed = false

        const onMove = (event: PointerEvent): void => {
            const x = event.clientX + Math.min(window.innerWidth * 0.14, 240)
            const y = event.clientY

            if (!placed) {
                placed = true
                gsap.set(preview, { x, y })
            }

            moveX(x)
            moveY(y)
        }

        const onEnter = (): void => setVisible(true)
        const onLeave = (): void => {
            placed = false
            setVisible(false)
        }

        list.addEventListener('pointermove', onMove)
        list.addEventListener('pointerenter', onEnter)
        list.addEventListener('pointerleave', onLeave)

        return () => {
            list.removeEventListener('pointermove', onMove)
            list.removeEventListener('pointerenter', onEnter)
            list.removeEventListener('pointerleave', onLeave)
        }
    }, [])

    const select = (index: number): void => {
        if (index === active) return
        setPrevious(active)
        setActive(index)
    }

    return (
        <section id='services' data-scene='paper' aria-labelledby='services-title' className='px-5 py-28 md:px-8 md:py-44'>
            <div className='mb-14 grid gap-8 lg:mb-20 lg:grid-cols-12'>
                <div className='lg:col-span-3'>
                    <SectionLabel index='03'>
                        Ways to work with us
                    </SectionLabel>
                </div>

                <SplitReveal as='h2' id='services-title' className='type-h1 lg:col-span-9'>
                    What do you want to <em className='type-serif'>build?</em>
                </SplitReveal>
            </div>

            <ul ref={listRef} className='border-t border-rule'>
                {services.map((service, index) => (
                    <li key={service.slug} className='border-b border-rule'>
                        <TransitionLink
                            href={`/services#${service.slug}`}
                            onPointerEnter={() => select(index)}
                            onFocus={() => select(index)}
                            className='group relative grid grid-cols-[2.25rem_1fr_auto] items-center gap-x-4 py-5 lg:grid-cols-12 lg:py-6'
                        >
                            <span
                                aria-hidden='true'
                                className='absolute inset-0 origin-left scale-x-0 bg-mark transition-transform duration-700 ease-quart group-hover:scale-x-100 group-focus-visible:scale-x-100 motion-reduce:transition-none'
                            />

                            <span className='type-label relative text-quiet transition-colors duration-500 group-hover:text-on-mark lg:col-span-1 lg:pl-2'>
                                0{index + 1}
                            </span>

                            <span className='type-h2 relative transition-[font-variation-settings,translate,color] duration-700 ease-quart [--wdth:100] group-hover:translate-x-3 group-hover:text-on-mark group-hover:[--wdth:125] lg:col-span-6 motion-reduce:transition-none'>
                                {service.name}
                            </span>

                            <span className='type-label relative hidden text-quiet transition-colors duration-500 group-hover:text-on-mark lg:col-span-4 lg:block'>
                                {service.situation}
                            </span>

                            <ArrowUpRight
                                aria-hidden='true'
                                className='relative size-6 justify-self-end transition-[translate,color] duration-500 ease-expo group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-on-mark lg:col-span-1 lg:mr-2'
                            />
                        </TransitionLink>
                    </li>
                ))}
            </ul>

            <div
                ref={previewRef}
                aria-hidden='true'
                className={cn(
                    'pointer-events-none fixed left-0 top-0 z-30 hidden w-[min(24vw,380px)] transition-[opacity,scale] duration-500 ease-expo lg:block',
                    visible ? 'scale-100 opacity-100' : 'scale-75 opacity-0'
                )}
            >
                <div className='relative aspect-[4/3] overflow-hidden bg-ink'>
                    {services.map((service, index) => (
                        <img
                            key={service.slug}
                            src={service.image}
                            alt=''
                            loading='lazy'
                            decoding='async'
                            className={cn(
                                'absolute inset-0 size-full object-cover',
                                index === active && 'z-[2] [clip-path:inset(0%_0%_0%_0%)] transition-[clip-path] duration-700 ease-quart',
                                index === previous && index !== active && 'z-[1]',
                                index !== active && index !== previous && 'z-0 [clip-path:inset(0%_0%_100%_0%)]'
                            )}
                        />
                    ))}
                </div>

                <CropMarks className='text-fore' />

                <p className='type-label mt-4 flex justify-between text-fore'>
                    <span>
                        Fig. 0{active + 1}
                    </span>

                    <span>
                        {services[active]?.platform}
                    </span>
                </p>
            </div>
        </section>
    )
}

export default ServicesIndex
